import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/client";
import {
  ClockIcon,
  ArrowRightIcon,
  UserIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  DocumentCheckIcon,
  UsersIcon,
  ChartBarIcon,
  CurrencyEuroIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  CreditCardIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";

interface DurationStat {
  avgDays: number;
  count: number;
}

interface DailyActiveUser {
  date: string;
  count: number;
}

interface DistributionBucket {
  bucket: string;
  count: number;
}

interface StepDistributions {
  registeredToDocuments: DistributionBucket[];
  registeredToBank: DistributionBucket[];
  registeredToContract: DistributionBucket[];
}

interface PayoutTierBucket {
  tier: string;
  contracted: number;
  pending: number;
}

interface OrderStats {
  totalOrders: number;
  gmvOrders: number;
  totalOrderValue: number;
  totalVolume: number;
  totalGMV: number;
  purchasedOrders: number;
  purchasedGMV: number;
}

interface DurationStats {
  registeredToDocuments: DurationStat;
  registeredToBank: DurationStat;
  registeredToContract: DurationStat;
}

interface DailyActiveStats {
  dailyActiveUsers: DailyActiveUser[];
}

interface DistributionStats {
  distributions: {
    allTime: StepDistributions;
    last28Days: StepDistributions;
  };
}

interface VolumeByTierBucket {
  tier: string;
  volume: number;
  percentage: number;
}

interface PayoutTierStats {
  payoutTiers: {
    allTime: PayoutTierBucket[];
    last28Days: PayoutTierBucket[];
    last7Days: PayoutTierBucket[];
  };
  volumeByTier: {
    allTime: VolumeByTierBucket[];
    last28Days: VolumeByTierBucket[];
    last7Days: VolumeByTierBucket[];
  };
}

interface OrderStatsResponse {
  orderStats: OrderStats;
}

interface ClaimantStage {
  stage: string;
  count: number;
  percentage: number;
}

interface UserOverTimeData {
  date: string;
  count: number;
  cumulative: number;
}

type TimeRange = 'allTime' | 'last28Days';
type PayoutTimeRange = 'allTime' | 'last28Days' | 'last7Days';
type StepFilter = 'all' | 'registeredToDocuments' | 'registeredToBank' | 'registeredToContract';

// Loading skeleton component
function LoadingSkeleton({ height = "h-56" }: { height?: string }) {
  return (
    <div className={`${height} animate-pulse bg-gray-200 dark:bg-gray-700 rounded`} />
  );
}

// Loading card component
function LoadingCard() {
  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden p-6">
      <div className="animate-pulse space-y-4">
        <div className="flex justify-center gap-4">
          <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
        </div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto" />
      </div>
    </div>
  );
}

export default function Stats() {
  const [distributionTimeRange, setDistributionTimeRange] = useState<TimeRange>('allTime');
  const [distributionStep, setDistributionStep] = useState<StepFilter>('all');
  const [payoutTimeRange, setPayoutTimeRange] = useState<PayoutTimeRange>('allTime');

  // Separate queries for each section
  const durationsQuery = useQuery({
    queryKey: ["statsDurations"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/durations");
      return res.data.data as DurationStats;
    },
  });

  const dailyActiveQuery = useQuery({
    queryKey: ["statsDailyActive"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/daily-active");
      return res.data.data as DailyActiveStats;
    },
  });

  const distributionsQuery = useQuery({
    queryKey: ["statsDistributions"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/distributions");
      return res.data.data as DistributionStats;
    },
  });

  const payoutTiersQuery = useQuery({
    queryKey: ["statsPayoutTiers"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/payout-tiers");
      return res.data.data as PayoutTierStats;
    },
  });

  const ordersQuery = useQuery({
    queryKey: ["statsOrders"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/orders");
      return res.data.data as OrderStatsResponse;
    },
  });

  const claimantsByStageQuery = useQuery({
    queryKey: ["statsClaimantsByStage"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/claimants-by-stage");
      return res.data.data.stages as ClaimantStage[];
    },
  });

  const usersOverTimeQuery = useQuery({
    queryKey: ["statsUsersOverTime"],
    queryFn: async () => {
      const res = await api.get("/admin/stats/users-over-time");
      return res.data.data as UserOverTimeData[];
    },
  });

  const formatDays = (days: number) => {
    if (days === 0) return "0 days";
    if (days < 1) {
      const hours = Math.round(days * 24);
      return hours === 1 ? "1 hour" : `${hours} hours`;
    }
    return days === 1 ? "1 day" : `${days.toFixed(1)} days`;
  };

  // Format date for display (DD.MM)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  // Format number with German locale
  const formatNumber = (num: number) => num.toLocaleString('de-DE');

  // Format currency
  const formatCurrency = (num: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);

  // Get distribution data based on filters
  const getDistributionData = () => {
    const data = distributionsQuery.data;
    if (!data?.distributions) return [];

    const timeData = data.distributions[distributionTimeRange];

    if (distributionStep === 'all') {
      // Combine all step distributions
      const combined: DistributionBucket[] = [];
      const bucketLabels = timeData.registeredToDocuments.map(b => b.bucket);

      for (const label of bucketLabels) {
        const regToDocs = timeData.registeredToDocuments.find(b => b.bucket === label)?.count ?? 0;
        const regToBank = timeData.registeredToBank.find(b => b.bucket === label)?.count ?? 0;
        const regToContract = timeData.registeredToContract.find(b => b.bucket === label)?.count ?? 0;
        combined.push({ bucket: label, count: regToDocs + regToBank + regToContract });
      }
      return combined;
    }

    return timeData[distributionStep];
  };

  // Build duration stats for rendering
  const stats = [
    {
      key: 'registeredToDocuments' as const,
      title: "Account Created to Documents Uploaded",
      shortTitle: "Register → Docs",
      from: { icon: UserIcon, label: "Account Created" },
      to: { icon: DocumentTextIcon, label: "Documents Uploaded" },
      avgDays: durationsQuery.data?.registeredToDocuments.avgDays ?? 0,
      count: durationsQuery.data?.registeredToDocuments.count ?? 0,
      color: "indigo",
    },
    {
      key: 'registeredToBank' as const,
      title: "Account Created to Banking Provided",
      shortTitle: "Register → Banking",
      from: { icon: UserIcon, label: "Account Created" },
      to: { icon: BuildingLibraryIcon, label: "Banking Provided" },
      avgDays: durationsQuery.data?.registeredToBank.avgDays ?? 0,
      count: durationsQuery.data?.registeredToBank.count ?? 0,
      color: "purple",
    },
    {
      key: 'registeredToContract' as const,
      title: "Account Created to Contract Completed",
      shortTitle: "Register → Contract",
      from: { icon: UserIcon, label: "Account Created" },
      to: { icon: DocumentCheckIcon, label: "Contract Completed" },
      avgDays: durationsQuery.data?.registeredToContract.avgDays ?? 0,
      count: durationsQuery.data?.registeredToContract.count ?? 0,
      color: "teal",
    },
  ];

  // Order stats calculations
  const orderStats = ordersQuery.data?.orderStats;
  const gmvOrdersPercent = orderStats && orderStats.totalOrders > 0
    ? ((orderStats.gmvOrders / orderStats.totalOrders) * 100).toFixed(1)
    : '0';
  const purchasedOrdersPercent = orderStats && orderStats.gmvOrders > 0
    ? ((orderStats.purchasedOrders / orderStats.gmvOrders) * 100).toFixed(1)
    : '0';

  // Daily active calculations
  const dailyActiveUsers = dailyActiveQuery.data?.dailyActiveUsers ?? [];
  const maxDailyActive = Math.max(...dailyActiveUsers.map(d => d.count), 1);
  const totalActiveUsers = dailyActiveUsers.reduce((sum, d) => sum + d.count, 0);
  const avgActiveUsers = dailyActiveUsers.length > 0
    ? Math.round(totalActiveUsers / dailyActiveUsers.length)
    : 0;

  // Distribution calculations
  const distributionData = getDistributionData();
  const maxDistribution = Math.max(...distributionData.map(d => d.count), 1);
  const totalDistribution = distributionData.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Funnel Analytics</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Average time between user progress steps
        </p>
      </div>

      {/* Order Statistics */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
          Order Statistics
        </h2>
        {ordersQuery.isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2" />
                <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : ordersQuery.error ? (
          <div className="text-red-500">Error loading order statistics</div>
        ) : (
          <div className="space-y-4">
            {/* Order Counts Row */}
            <div className="grid grid-cols-3 gap-4">
              {/* Total Orders */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingBagIcon className="h-4 w-4 text-purple-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Orders</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(orderStats?.totalOrders ?? 0)}
                </div>
              </div>

              {/* Eligible Orders */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircleIcon className="h-4 w-4 text-teal-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Eligible Orders</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(orderStats?.gmvOrders ?? 0)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {gmvOrdersPercent}% of total
                </div>
              </div>

              {/* Purchased Orders */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DocumentCheckIcon className="h-4 w-4 text-green-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Purchased Orders</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(orderStats?.purchasedOrders ?? 0)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {purchasedOrdersPercent}% of Eligible
                </div>
              </div>
            </div>

            {/* Volume/GMV Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Total Volume */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingBagIcon className="h-4 w-4 text-purple-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Volume</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(orderStats?.totalOrderValue ?? 0)}
                </div>
              </div>

              {/* Eligible Volume */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCardIcon className="h-4 w-4 text-cyan-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Eligible Volume</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(orderStats?.totalVolume ?? 0)}
                </div>
              </div>

              {/* Total GMV */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyEuroIcon className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total GMV</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(orderStats?.totalGMV ?? 0)}
                </div>
              </div>

              {/* Purchased GMV */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyEuroIcon className="h-4 w-4 text-green-500" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Purchased GMV</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(orderStats?.purchasedGMV ?? 0)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {orderStats && orderStats.totalGMV > 0
                    ? ((orderStats.purchasedGMV / orderStats.totalGMV) * 100).toFixed(1)
                    : '0'}% of total GMV
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Duration Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {durationsQuery.isLoading ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : durationsQuery.error ? (
          <div className="col-span-3 text-red-500">Error loading duration statistics</div>
        ) : (
          stats.map((stat) => (
            <div
              key={stat.title}
              className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
            >
              <div className="p-6">
                {/* Step Flow */}
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="flex flex-col items-center">
                    <div className={`p-2 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                      <stat.from.icon className={`h-5 w-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                    </div>
                    <span className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {stat.from.label}
                    </span>
                  </div>
                  <ArrowRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="flex flex-col items-center">
                    <div className={`p-2 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                      <stat.to.icon className={`h-5 w-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                    </div>
                    <span className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                      {stat.to.label}
                    </span>
                  </div>
                </div>

                {/* Average Days */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <ClockIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      {formatDays(stat.avgDays)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">average duration</p>
                </div>

                {/* Sample Size */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-center">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Based on <span className="font-medium text-gray-900 dark:text-white">{stat.count.toLocaleString('de-DE')}</span> users
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Distribution Chart */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
              Duration Distribution
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How many users completed each transition within time buckets
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Time Range Filter */}
            <select
              value={distributionTimeRange}
              onChange={(e) => setDistributionTimeRange(e.target.value as TimeRange)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="allTime">All Time</option>
              <option value="last28Days">Last 28 Days</option>
            </select>
            {/* Step Filter */}
            <select
              value={distributionStep}
              onChange={(e) => setDistributionStep(e.target.value as StepFilter)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Steps Combined</option>
              {stats.map((stat) => (
                <option key={stat.key} value={stat.key}>{stat.shortTitle}</option>
              ))}
            </select>
          </div>
        </div>

        {distributionsQuery.isLoading ? (
          <LoadingSkeleton />
        ) : distributionsQuery.error ? (
          <div className="text-red-500">Error loading distribution data</div>
        ) : (
          /* Bar Chart */
          <div className="relative">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
              <span className="text-right">{maxDistribution.toLocaleString('de-DE')}</span>
              <span className="text-right">{Math.round(maxDistribution / 2).toLocaleString('de-DE')}</span>
              <span className="text-right">0</span>
            </div>

            {/* Chart area */}
            <div className="ml-14">
              <div className="flex items-end gap-2 h-56 border-b border-l border-gray-200 dark:border-gray-700 pb-1 pl-1">
                {distributionData.map((bucket) => {
                  const height = maxDistribution > 0 ? (bucket.count / maxDistribution) * 100 : 0;
                  const percentage = totalDistribution > 0 ? ((bucket.count / totalDistribution) * 100).toFixed(1) : '0';
                  return (
                    <div
                      key={bucket.bucket}
                      className="flex-1 h-full flex flex-col items-center justify-end group relative"
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          {bucket.bucket}: {bucket.count.toLocaleString('de-DE')} users ({percentage}%)
                        </div>
                      </div>
                      {/* Bar */}
                      <div
                        className="w-full rounded-t bg-indigo-500 dark:bg-indigo-400 hover:bg-indigo-600 dark:hover:bg-indigo-300 transition-colors"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      {/* X-axis label */}
                      <span className="absolute -bottom-6 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {bucket.bucket}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Stats summary */}
              <div className="flex items-center justify-end gap-6 mt-8 text-sm text-gray-500 dark:text-gray-400">
                <div>
                  Total: <span className="font-medium text-gray-900 dark:text-white">{totalDistribution.toLocaleString('de-DE')}</span> users
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payout Tier Distribution */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
              Payout Tier Distribution
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Contracted vs pending users by payout tier (documents uploaded)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={payoutTimeRange}
              onChange={(e) => setPayoutTimeRange(e.target.value as PayoutTimeRange)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="allTime">All Time</option>
              <option value="last28Days">Last 28 Days</option>
              <option value="last7Days">Last 7 Days</option>
            </select>
          </div>
        </div>

        {payoutTiersQuery.isLoading ? (
          <LoadingSkeleton />
        ) : payoutTiersQuery.error ? (
          <div className="text-red-500">Error loading payout tier data</div>
        ) : (
          /* Stacked Bar Chart */
          (() => {
            const payoutData = payoutTiersQuery.data?.payoutTiers?.[payoutTimeRange] ?? [];
            const maxPayout = Math.max(...payoutData.map(d => d.contracted + d.pending), 1);
            const totalContracted = payoutData.reduce((sum, d) => sum + d.contracted, 0);
            const totalPending = payoutData.reduce((sum, d) => sum + d.pending, 0);

            return (
              <div className="relative">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
                  <span className="text-right">{maxPayout.toLocaleString('de-DE')}</span>
                  <span className="text-right">{Math.round(maxPayout / 2).toLocaleString('de-DE')}</span>
                  <span className="text-right">0</span>
                </div>

                {/* Chart area */}
                <div className="ml-14">
                  <div className="flex items-end gap-3 h-56 border-b border-l border-gray-200 dark:border-gray-700 pb-1 pl-1">
                    {payoutData.map((tier) => {
                      const total = tier.contracted + tier.pending;
                      const heightTotal = maxPayout > 0 ? (total / maxPayout) * 100 : 0;
                      const contractedRatio = total > 0 ? (tier.contracted / total) * 100 : 0;
                      return (
                        <div
                          key={tier.tier}
                          className="flex-1 h-full flex flex-col items-center justify-end group relative"
                        >
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                              <div>{tier.tier}</div>
                              <div className="text-green-400">Contracted: {tier.contracted.toLocaleString('de-DE')}</div>
                              <div className="text-amber-400">Pending: {tier.pending.toLocaleString('de-DE')}</div>
                            </div>
                          </div>
                          {/* Stacked Bar */}
                          <div
                            className="w-full flex flex-col rounded-t overflow-hidden"
                            style={{ height: `${Math.max(heightTotal, 2)}%` }}
                          >
                            {/* Pending (top) */}
                            <div
                              className="w-full bg-amber-400 dark:bg-amber-500"
                              style={{ height: `${100 - contractedRatio}%` }}
                            />
                            {/* Contracted (bottom) */}
                            <div
                              className="w-full bg-green-500 dark:bg-green-400"
                              style={{ height: `${contractedRatio}%` }}
                            />
                          </div>
                          {/* X-axis label */}
                          <span className="absolute -bottom-6 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {tier.tier}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Legend and stats */}
                  <div className="flex items-center justify-between mt-8">
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-green-500 dark:bg-green-400" />
                        <span>Contracted ({totalContracted.toLocaleString('de-DE')})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-amber-400 dark:bg-amber-500" />
                        <span>Pending ({totalPending.toLocaleString('de-DE')})</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Total: <span className="font-medium text-gray-900 dark:text-white">{(totalContracted + totalPending).toLocaleString('de-DE')}</span> users
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

        {/* Volume Distribution Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 mt-8 pt-6">
          <div className="mb-4">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              Volume Distribution (Contracted Only)
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Percentage of total payout volume by tier
            </p>
          </div>

          {payoutTiersQuery.isLoading ? (
            <LoadingSkeleton />
          ) : payoutTiersQuery.error ? (
            <div className="text-red-500">Error loading volume data</div>
          ) : (
            (() => {
              const volumeData = payoutTiersQuery.data?.volumeByTier?.[payoutTimeRange] ?? [];
              const totalVolume = volumeData.reduce((sum, d) => sum + d.volume, 0);

              return (
                <div className="space-y-3">
                  {volumeData.map((tier) => (
                    <div key={tier.tier} className="group">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-700 dark:text-gray-300">{tier.tier}</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {tier.volume.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} ({tier.percentage}%)
                        </span>
                      </div>
                      <div className="relative h-6 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-indigo-500 dark:bg-indigo-400 rounded transition-all duration-300"
                          style={{ width: `${tier.percentage}%` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-900 dark:text-white">
                          {tier.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-right mt-4">
                    Total Volume: <span className="font-medium text-gray-900 dark:text-white">{totalVolume.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>

      {/* Claimants by Stage */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            Claimants by Stage
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            User progression through the funnel
          </p>
        </div>

        {claimantsByStageQuery.isLoading ? (
          <LoadingSkeleton />
        ) : claimantsByStageQuery.error ? (
          <div className="text-red-500">Error loading claimants by stage data</div>
        ) : (
          <div className="space-y-2">
            {(claimantsByStageQuery.data ?? []).map((stage, index) => {
              const colors = [
                'bg-indigo-500 dark:bg-indigo-400',
                'bg-purple-500 dark:bg-purple-400',
                'bg-blue-500 dark:bg-blue-400',
                'bg-cyan-500 dark:bg-cyan-400',
                'bg-teal-500 dark:bg-teal-400',
                'bg-green-500 dark:bg-green-400',
                'bg-lime-500 dark:bg-lime-400',
                'bg-emerald-500 dark:bg-emerald-400',
              ];
              return (
                <div key={stage.stage} className="group">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">{stage.stage}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {stage.count.toLocaleString('de-DE')} users ({stage.percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="relative h-6 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${colors[index % colors.length]} rounded transition-all duration-300`}
                      style={{ width: `${stage.percentage}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-900 dark:text-white">
                      {stage.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily Active Users Chart */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-gray-400" />
              Daily Active Users
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Users with progress steps in the last 28 days
            </p>
          </div>
          {!dailyActiveQuery.isLoading && !dailyActiveQuery.error && (
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{avgActiveUsers}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">avg/day</div>
            </div>
          )}
        </div>

        {dailyActiveQuery.isLoading ? (
          <LoadingSkeleton height="h-48" />
        ) : dailyActiveQuery.error ? (
          <div className="text-red-500">Error loading daily active users</div>
        ) : (
          /* Bar Chart */
          <div className="relative">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-6 w-10 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
              <span className="text-right">{maxDailyActive}</span>
              <span className="text-right">{Math.round(maxDailyActive / 2)}</span>
              <span className="text-right">0</span>
            </div>

            {/* Chart area */}
            <div className="ml-12">
              <div className="flex items-end gap-1 h-48 border-b border-l border-gray-200 dark:border-gray-700 pb-1 pl-1">
                {dailyActiveUsers.map((day, index) => {
                  const height = maxDailyActive > 0 ? (day.count / maxDailyActive) * 100 : 0;
                  const isWeekend = new Date(day.date).getDay() === 0 || new Date(day.date).getDay() === 6;
                  return (
                    <div
                      key={day.date}
                      className="flex-1 h-full flex flex-col items-center justify-end group relative"
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          {formatDate(day.date)}: {day.count} users
                        </div>
                      </div>
                      {/* Bar */}
                      <div
                        className={`w-full rounded-t transition-all ${
                          isWeekend
                            ? 'bg-gray-300 dark:bg-gray-600'
                            : 'bg-indigo-500 dark:bg-indigo-400'
                        } hover:opacity-80`}
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      {/* X-axis label - show every 7th day */}
                      {index % 7 === 0 && (
                        <span className="absolute -bottom-5 text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(day.date)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div className="flex items-center justify-end gap-4 mt-8 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-indigo-500 dark:bg-indigo-400" />
                  <span>Weekday</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-gray-300 dark:bg-gray-600" />
                  <span>Weekend</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Total Users Over Time Chart */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-gray-400" />
              Total Users Over Time
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Cumulative user registrations since launch
            </p>
          </div>
          {!usersOverTimeQuery.isLoading && !usersOverTimeQuery.error && usersOverTimeQuery.data && (
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(usersOverTimeQuery.data[usersOverTimeQuery.data.length - 1]?.cumulative ?? 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">total users</div>
            </div>
          )}
        </div>

        {usersOverTimeQuery.isLoading ? (
          <LoadingSkeleton height="h-48" />
        ) : usersOverTimeQuery.error ? (
          <div className="text-red-500">Error loading users over time data</div>
        ) : (
          (() => {
            const data = usersOverTimeQuery.data ?? [];
            const maxCumulative = Math.max(...data.map(d => d.cumulative), 1);

            // Sample data for display (show ~60 points max for readability)
            const sampleRate = Math.max(1, Math.floor(data.length / 60));
            const sampledData = data.filter((_, i) => i % sampleRate === 0 || i === data.length - 1);

            return (
              <div className="relative">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
                  <span className="text-right">{formatNumber(maxCumulative)}</span>
                  <span className="text-right">{formatNumber(Math.round(maxCumulative / 2))}</span>
                  <span className="text-right">0</span>
                </div>

                {/* Chart area */}
                <div className="ml-14">
                  <div className="flex items-end gap-0.5 h-48 border-b border-l border-gray-200 dark:border-gray-700 pb-1 pl-1">
                    {sampledData.map((day, index) => {
                      const height = maxCumulative > 0 ? (day.cumulative / maxCumulative) * 100 : 0;
                      return (
                        <div
                          key={day.date}
                          className="flex-1 h-full flex flex-col items-center justify-end group relative"
                        >
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                              {formatDate(day.date)}: {formatNumber(day.cumulative)} total (+{day.count})
                            </div>
                          </div>
                          {/* Bar */}
                          <div
                            className="w-full rounded-t bg-emerald-500 dark:bg-emerald-400 hover:bg-emerald-600 dark:hover:bg-emerald-300 transition-colors"
                            style={{ height: `${Math.max(height, 1)}%` }}
                          />
                          {/* X-axis label - show start, middle, end */}
                          {(index === 0 || index === Math.floor(sampledData.length / 2) || index === sampledData.length - 1) && (
                            <span className="absolute -bottom-5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {formatDate(day.date)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Stats summary */}
                  <div className="flex items-center justify-end gap-6 mt-8 text-sm text-gray-500 dark:text-gray-400">
                    <div>
                      First registration: <span className="font-medium text-gray-900 dark:text-white">{data[0] ? formatDate(data[0].date) : '-'}</span>
                    </div>
                    <div>
                      Days active: <span className="font-medium text-gray-900 dark:text-white">{data.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}
